/**
 * product-group-swatches.js
 *
 * Groups products by tag product-group--{name}.
 * On PLP: fetches sibling handles + full product data via Storefront API GraphQL,
 *         renders colour swatches and builds card HTML in JS for instant swap on click.
 * On PDP: same API calls, renders <a href> colour swatches (page navigation).
 */
if (!window.GroupSwatches) {
  const STOREFRONT_API_VERSION = '2026-01';
  const VISIBLE_LIMIT = 6;

  const WISHLIST_ICON = '<svg width="29" height="34" viewBox="0 0 29 34" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M27.6667 31.5987L14.3333 20.2134L1 31.5987V1.00003H27.6667V31.5987Z" stroke="black" stroke-width="2" stroke-miterlimit="10"/></svg>';

  // tag -> handles[]
  const groupHandles = new Map();
  // tag -> Promise<handles[]>
  const groupFetching = new Map();
  // handle -> { colour, swatchUrl }
  const swatchData = new Map();
  // handle -> rendered swatch HTML fallback
  const swatchMarkup = new Map();
  // handle -> full product data object
  const productData = new Map();

  const getToken = () => window.theme?.storefrontAccessToken ?? '';
  const getDomain = () => window.theme?.shopDomain ?? location.hostname;

  /* ── helpers ─────────────────────────────────────────────── */

  const escapeHtml = (str) => {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  };

  const handleize = (str) =>
    String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const extractId = (gid) => String(gid).split('/').pop();

  const formatPrice = (amount) => {
    if (!amount) return '';
    return String(amount).replace(/\.00$/, '');
  };

  const buildSwatchHtml = (handle) => {
    const data = swatchData.get(handle);
    if (data?.colour) return `<span style="background-color: ${data.colour}"></span>`;
    if (data?.swatchUrl) return `<img src="${data.swatchUrl}" alt="" width="18" height="18" loading="lazy" />`;
    return swatchMarkup.get(handle) ?? '<span></span>';
  };

  // Normalizes rendered swatch markup and keeps it available for later re-renders on the same page.
  const cacheSwatchMarkup = (handle, markup) => {
    const trimmedMarkup = markup?.trim();
    if (handle && trimmedMarkup && trimmedMarkup !== '<span></span>') {
      swatchMarkup.set(handle, trimmedMarkup);
    }
    return trimmedMarkup === '<span></span>' ? '' : trimmedMarkup ?? '';
  };

  const collectRenderedSwatchMarkup = (root = document) => {
    root.querySelectorAll('.main-color-variant .color-var[data-handle]').forEach((swatchEl) => {
      cacheSwatchMarkup(swatchEl.dataset.handle, swatchEl.innerHTML ?? '');
    });
  };

  const isUrlLike = (value) => /^(https?:)?\/\//.test(value) || value.startsWith('/');

  const extractUrlFromSwatchEntry = (entry) => {
    if (!entry) return '';

    if (typeof entry === 'string') {
      const trimmedEntry = entry.trim();
      return isUrlLike(trimmedEntry) ? trimmedEntry : '';
    }

    if (typeof entry !== 'object') return '';

    return entry.src ?? entry.url ?? entry.image?.src ?? entry.image?.url ?? entry.file?.url ?? '';
  };

  const extractUrlFromSwatchValue = (rawValue) => {
    const trimmedValue = String(rawValue ?? '').trim();
    if (!trimmedValue) return '';

    const directUrl = extractUrlFromSwatchEntry(trimmedValue);
    if (directUrl) return directUrl;

    try {
      const parsedValue = JSON.parse(trimmedValue);
      const firstEntry = Array.isArray(parsedValue) ? parsedValue[0] : parsedValue;
      return extractUrlFromSwatchEntry(firstEntry);
    } catch (err) {
      return '';
    }
  };

  const extractSwatchUrl = (swatchMetafield) => {
    const ref = swatchMetafield?.reference;
    const listRef = swatchMetafield?.references?.nodes?.[0];
    const directUrl = ref?.image?.url ?? ref?.url ?? listRef?.image?.url ?? listRef?.url ?? '';
    if (directUrl) return directUrl;

    return extractUrlFromSwatchValue(swatchMetafield?.value);
  };

  const normalizeGroupHandles = (handles) =>
    Array.from(new Set((handles ?? []).filter(Boolean)));

  const getVisibleHandles = (handles, currentHandle) => {
    const ordered = normalizeGroupHandles(handles);
    const visible = ordered.slice(0, VISIBLE_LIMIT);
    if (currentHandle && ordered.includes(currentHandle) && !visible.includes(currentHandle) && visible.length > 0) {
      visible[visible.length - 1] = currentHandle;
    }
    return Array.from(new Set(visible));
  };

  const getCollectionPath = () => {
    const cfg = window.theme?.collectionPath;
    if (cfg) return cfg;
    const m = window.location.pathname.match(/^(\/collections\/[^/]+)/);
    return m ? m[1] : '';
  };

  const getProductUrl = (handle) => {
    const cp = getCollectionPath();
    return cp ? `${cp}/products/${handle}` : `/products/${handle}`;
  };

  /* ── GraphQL ─────────────────────────────────────────────── */

  const GRAPHQL_QUERY = `query ProductsByTag($query: String!) {
    products(first: 20, query: $query) {
      edges {
        node {
          id
          handle
          title
          tags
          options { name }
          images(first: 3) {
            edges { node { url(transform: {maxWidth: 600}) altText } }
          }
          media(first: 5) {
            edges {
              node {
                mediaContentType
                previewImage { url(transform: {maxWidth: 600}) }
                ... on MediaImage { image { url(transform: {maxWidth: 600}) } }
                ... on Video { sources { url mimeType } }
                ... on ExternalVideo { host embeddedUrl }
              }
            }
          }
          variants(first: 30) {
            edges {
              node {
                id
                title
                availableForSale
                selectedOptions { name value }
                price { amount currencyCode }
                compareAtPrice { amount currencyCode }
              }
            }
          }
          priceRange { minVariantPrice { amount currencyCode } }
          compareAtPriceRange { maxVariantPrice { amount currencyCode } }
          colour: metafield(namespace: "accentuate", key: "colour") { value }
          swatch: metafield(namespace: "accentuate", key: "swatch") {
            value
            reference {
              ... on MediaImage { image { url(transform: {maxWidth: 36}) } }
              ... on GenericFile { url }
            }
            references(first: 1) {
              nodes {
                ... on MediaImage { image { url(transform: {maxWidth: 36}) } }
                ... on GenericFile { url }
              }
            }
          }
          colorMeta: metafield(namespace: "custom", key: "color") { value }
        }
      }
    }
  }`;

  const fetchGroup = async (tag) => {
    if (groupHandles.has(tag)) return groupHandles.get(tag);
    if (groupFetching.has(tag)) return groupFetching.get(tag);

    const token = getToken();
    const domain = getDomain();
    if (!token || !domain) {
      console.warn('[GroupSwatches] Missing storefrontAccessToken or shopDomain.');
      return [];
    }

    const storefrontQuery = `tag:"${String(tag).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

    const request = (async () => {
      try {
        const res = await fetch(`https://${domain}/api/${STOREFRONT_API_VERSION}/graphql.json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query: GRAPHQL_QUERY, variables: { query: storefrontQuery } }),
        });
        if (!res.ok) throw new Error(`Storefront API ${res.status}`);

        const json = await res.json();
        const edges = json?.data?.products?.edges ?? [];
        const handles = edges
          .map((edge) => edge?.node)
          .filter((node) => node?.handle && Array.isArray(node.tags) && node.tags.includes(tag))
          .map((node) => {
            // Swatch data
            const colour = node.colour?.value ?? '';
            const swatchUrl = extractSwatchUrl(node.swatch);
            swatchData.set(node.handle, { colour, swatchUrl });

            // Full product data
            const images = (node.images?.edges ?? []).map((e) => e.node);
            const media = (node.media?.edges ?? []).map((e) => e.node);
            const variants = (node.variants?.edges ?? []).map((e) => e.node);

            productData.set(node.handle, {
              id: node.id,
              handle: node.handle,
              title: node.title,
              tags: node.tags,
              options: (node.options ?? []).map((o) => o.name),
              images,
              media,
              variants,
              price: node.priceRange?.minVariantPrice?.amount ?? '',
              compareAtPrice: node.compareAtPriceRange?.maxVariantPrice?.amount ?? '',
              currencyCode: node.priceRange?.minVariantPrice?.currencyCode ?? '',
              colorMeta: node.colorMeta?.value ?? '',
            });

            return node.handle;
          });

        groupHandles.set(tag, handles);
        return handles;
      } catch (err) {
        console.error('[GroupSwatches] fetchGroup error:', err);
        return [];
      } finally {
        groupFetching.delete(tag);
      }
    })();

    groupFetching.set(tag, request);
    return request;
  };

  /* ── JS card builder ─────────────────────────────────────── */

  const buildCardInnerHtml = (handle) => {
    const data = productData.get(handle);
    if (!data) return '';

    const cfg = window.theme ?? {};
    const currencyCode = data.currencyCode || cfg.currencyCode || '';
    const badges = cfg.badges ?? [];
    const enableHover = cfg.enableImageHover ?? false;
    const showSizes = cfg.showSizeSelector ?? false;
    const detailSelector = cfg.sizeSelector === 'go_to_detail';
    const showQuickbuyMobile = cfg.showQuickbuyMobile ?? false;
    const customerId = cfg.customerId ?? '';
    const quickAddText = cfg.quickAddText ?? 'Quick add';
    const productUrl = getProductUrl(handle);

    // ── Badges ──
    let badgeHtml = '';
    const tagHandles = data.tags.map((t) => handleize(t));
    const matchedBadges = [];
    for (const b of badges) {
      const idx = tagHandles.indexOf(b.tag);
      if (idx !== -1) matchedBadges.push({ label: data.tags[idx], color: b.color, bg: b.bg });
    }
    if (matchedBadges.length) {
      badgeHtml = '<div class="grid-badge">' +
        matchedBadges.map((b) => `<span style="color:${escapeHtml(b.color)};background:${escapeHtml(b.bg)}">${escapeHtml(b.label)}</span>`).join('') +
        '</div>';
    }

    // ── Display tag ──
    let displayTagHtml = '';
    for (const tag of data.tags) {
      const tl = tag.toLowerCase();
      if (tl.includes('displaytag-') && tl.includes('boxdisplaytag-')) {
        displayTagHtml = `<div class="display-tag box-display-tag">${escapeHtml(tag.split('-').pop())}</div>`;
        break;
      } else if (tl.includes('displaytag-')) {
        displayTagHtml = `<div class="display-tag">${escapeHtml(tag.split('-').pop())}</div>`;
        break;
      } else if (tl.includes('boxdisplaytag-')) {
        displayTagHtml = `<div class="display-tag box-display-tag">${escapeHtml(tag.split('-').pop())}</div>`;
        break;
      }
    }

    // ── Images & video ──
    const images = data.images;
    const media = data.media;
    const featuredImage = images[0] ?? null;
    const videoMedia = media.find((m) => m.mediaContentType === 'VIDEO' || m.mediaContentType === 'EXTERNAL_VIDEO') ?? null;
    const hoverMedia = media[1] ?? null;

    let imagesHtml = '';

    const buildHoverHtml = () => {
      if (!enableHover || !hoverMedia) return '';
      const url = hoverMedia.image?.url ?? hoverMedia.previewImage?.url ?? '';
      return url ? `<img src="${url}" loading="lazy" class="second-image" />` : '';
    };

    if (videoMedia) {
      const posterUrl = featuredImage?.url ?? videoMedia.previewImage?.url ?? '';

      let posterHtml = '';
      if (posterUrl) {
        posterHtml = `<img src="${posterUrl}" loading="lazy" class="product-card-poster" />`;
      }

      let videoPlaceholderHtml = '';
      if (videoMedia.mediaContentType === 'EXTERNAL_VIDEO') {
        const host = (videoMedia.host ?? '').toLowerCase();
        let videoSrc = videoMedia.embeddedUrl ?? '';
        if (host === 'youtube') {
          const videoId = videoSrc.split('/').pop().split('?')[0];
          videoSrc += (videoSrc.includes('?') ? '&' : '?') + 'autoplay=1&loop=1&playlist=' + videoId;
        } else {
          videoSrc += (videoSrc.includes('?') ? '&' : '?') + 'autoplay=1&loop=1';
        }
        videoPlaceholderHtml = `<div class="product-card-video-placeholder" data-video-type="external" data-video-host="${escapeHtml(host)}" data-video-src="${escapeHtml(videoSrc)}"></div>`;
      } else {
        const sources = (videoMedia.sources ?? []).map((s) => ({ url: s.url, mime_type: s.mimeType }));
        videoPlaceholderHtml = `<div class="product-card-video-placeholder" data-video-type="native" data-video-sources='${JSON.stringify(sources)}'></div>`;
      }

      imagesHtml = posterHtml + videoPlaceholderHtml + buildHoverHtml();
    } else if (featuredImage) {
      imagesHtml =
        `<img src="${featuredImage.url}" loading="lazy" />` +
        buildHoverHtml();
    } else {
      imagesHtml = '<svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"></svg>';
    }

    // ── Size selector ──
    let variantHtml = '';
    if (showSizes) {
      const sizeOptionIdx = data.options.findIndex((o) => o.toLowerCase().includes('size'));
      const mobileClass = showQuickbuyMobile ? '' : 'hide-variant-mobile';
      const listClass = detailSelector ? '' : 'direct-cart-list';

      let itemsHtml = '';
      if (sizeOptionIdx >= 0) {
        itemsHtml = data.variants.map((v) => {
          const value = v.selectedOptions[sizeOptionIdx]?.value ?? v.title;
          const numId = extractId(v.id);
          const soldOut = v.availableForSale ? '' : 'sold-out';
          const inner = detailSelector
            ? `<a href="${productUrl}?variant=${numId}">${escapeHtml(value)}</a>`
            : escapeHtml(value);
          return `<li data-id="${numId}" class="variant-grid btn-link ${soldOut}">${inner}</li>`;
        }).join('');
      } else {
        const fv = data.variants[0];
        const numId = fv ? extractId(fv.id) : '';
        const soldOut = fv?.availableForSale ? '' : 'sold-out';
        itemsHtml = `<li data-id="${numId}" class="variant-grid btn-link ${soldOut}">OS</li>`;
      }

      variantHtml = `<div class="grid-variant-outer ${mobileClass}"><div class="grid-error"></div><div class="grid-quick-add line-18"><span class="grid-quick btn-white">${escapeHtml(quickAddText)}</span></div><div class="grid-var"><ul class="grid-size-variants list--inline ${listClass}">${itemsHtml}</ul></div></div>`;
    }

    // ── Wishlist ──
    const numProdId = extractId(data.id);
    const firstVarId = data.variants[0] ? extractId(data.variants[0].id) : '';
    const wishlistBarItems = data.variants.map((v) => {
      const vid = extractId(v.id);
      const cls = v.availableForSale ? '' : 'sold-out-var';
      return `<li class="btn-link var ${cls}" data-id="${vid}">${escapeHtml(v.title)}</li>`;
    }).join('');

    const wishlistHtml =
      `<div class="wishlist-bar"><ul class="">${wishlistBarItems}</ul></div>` +
      `<button class="wishlist-btn" customer-id="${escapeHtml(customerId)}" data-product-id="${numProdId}" data-product-handle="${handle}" data-product-title="${escapeHtml(data.title)}"><span>Add to Wishlist</span>${WISHLIST_ICON}</button>` +
      `<div class="th_wl_col_btn" data-product_id="${numProdId}" data-variant_id="${firstVarId}"></div>`;

    // ── Title ──
    const mainTitle = data.title.replace(/- /g, ' - ').replace(/ _/g, ' _ ').split(' - ')[0].trim();

    // ── Color name ──
    let colorName = '';
    for (const tag of data.tags) {
      if (tag.startsWith('Colour-') || tag.startsWith('colour-')) {
        colorName = tag.replace(/^[Cc]olour-/, '');
        break;
      }
    }
    if (!colorName) colorName = data.colorMeta;

    // ── Price ──
    const priceFormatted = formatPrice(data.price);
    let comparePriceHtml = '';
    if (data.compareAtPrice && parseFloat(data.compareAtPrice) > parseFloat(data.price)) {
      comparePriceHtml = ` <span class="compare-price">${formatPrice(data.compareAtPrice)} ${escapeHtml(currencyCode)}</span>`;
    }

    // ── Meta ──
    const metaHtml = `<div class="grid-meta">
      <p class="grid-pro-title"><a class="line-18" href="${productUrl}">${escapeHtml(mainTitle)}</a></p>
      <div class="grid-color-info line-18">
        <span class="color-count line-18">1 colour</span>
        <span class="color-name">${escapeHtml(colorName)}</span>
      </div>
      <div class="grid-bottom">
        <div class="grid-pro-color">
          <div class="main-color-variant" data-handle="${handle}">
            <span class="color-var active" data-handle="${handle}">${buildSwatchHtml(handle)}</span>
          </div>
        </div>
        <div class="grid-pro-price">
          <p class="line-18">${priceFormatted} ${escapeHtml(currencyCode)}${comparePriceHtml}</p>
        </div>
      </div>
    </div>`;

    // ── Assemble ──
    return `<div class="inner-product-grid active">
      <div class="grid-imagebox">
        <a href="${productUrl}" class="product-link">
          ${badgeHtml}
          <div class="product-images">
            ${displayTagHtml}
            ${imagesHtml}
          </div>
        </a>
        ${variantHtml}
        ${wishlistHtml}
      </div>
      ${metaHtml}
    </div>`;
  };

  /* ── Swatch rendering ────────────────────────────────────── */

  const renderSwatches = (innerGrid, handles, currentHandle) => {
    const container = innerGrid.querySelector('.main-color-variant');
    if (!container) return;

    const normalizedHandles = normalizeGroupHandles(handles);
    const visibleHandles = getVisibleHandles(handles, currentHandle);

    const colorCount = innerGrid.querySelector('.color-count');
    if (colorCount) {
      const n = normalizedHandles.length;
      const one = window.variantStrings?.colour ?? 'colour';
      const many = window.variantStrings?.colours ?? 'colours';
      colorCount.textContent = `${n} ${n === 1 ? one : many}`;
    }

    const activeMarkup = cacheSwatchMarkup(
      currentHandle,
      container.querySelector('.color-var.active')?.innerHTML ?? ''
    );
    const fragment = document.createDocumentFragment();
    const swatches = visibleHandles.map((handle) => {
      const swatchEl = document.createElement('span');
      swatchEl.className = `color-var ${handle === currentHandle ? 'active' : 'click'}`;
      swatchEl.dataset.handle = handle;
      swatchEl.innerHTML = handle === currentHandle ? activeMarkup : buildSwatchHtml(handle);
      return swatchEl;
    });

    swatches.forEach((s) => fragment.appendChild(s));

    if (normalizedHandles.length > VISIBLE_LIMIT) {
      const more = document.createElement('span');
      more.className = 'more';
      more.textContent = `+${normalizedHandles.length - VISIBLE_LIMIT}`;
      fragment.appendChild(more);
    }

    container.replaceChildren(fragment);
  };

  /* ── Card swap (instant — no network request) ──────────── */

  const swapCard = (snippetEl, newHandle, handles) => {
    const html = buildCardInnerHtml(newHandle);
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInnerGrid = doc.querySelector('.inner-product-grid');
    if (!newInnerGrid) return;

    const oldInnerGrid = snippetEl.querySelector('.inner-product-grid.active');
    if (!oldInnerGrid) return;

    oldInnerGrid.replaceWith(newInnerGrid);
    snippetEl.dataset.id = newHandle;
    renderSwatches(newInnerGrid, handles, newHandle);

    if (typeof $th_refreshWishlistItems === 'function') {
      $th_refreshWishlistItems();
    }
  };

  /* ── PLP init ────────────────────────────────────────────── */

  const processTagCards = async (cardEls, tag) => {
    const handles = await fetchGroup(tag);
    if (!handles || handles.length < 2) return;

    cardEls.forEach((cardEl) => {
      const innerGrid = cardEl.querySelector('.inner-product-grid.active');
      if (!innerGrid) return;
      const currentHandle = cardEl.dataset.id;
      renderSwatches(innerGrid, normalizeGroupHandles(handles), currentHandle);
    });
  };

  const init = (root = document) => {
    collectRenderedSwatchMarkup(root);

    const cards = Array.from(root.querySelectorAll('.snippet-product-grid[data-group-tag]'));
    const tagMap = new Map();

    cards.forEach((card) => {
      const tag = card.dataset.groupTag;
      if (!tag) return;
      if (!tagMap.has(tag)) tagMap.set(tag, []);
      tagMap.get(tag).push(card);
    });

    tagMap.forEach((cardEls, tag) => {
      processTagCards(cardEls, tag).catch((err) => {
        console.error('[GroupSwatches] init error:', err);
      });
    });
  };

  /* ── PDP init ────────────────────────────────────────────── */

  const initPdp = () => {
    const container = document.querySelector('.snippet-color-selector .main-color-variant[data-group-tag]');
    if (!container) return;

    const tag = container.dataset.groupTag;
    if (!tag) return;

    const currentHandle = container.dataset.currentHandle;

    const runPdpInit = async () => {
      collectRenderedSwatchMarkup(container.closest('.snippet-color-selector') ?? document);

      const handles = await fetchGroup(tag);
      if (!handles || handles.length < 2) return;

      const normalizedHandles = normalizeGroupHandles([...(handles ?? []), currentHandle]);
      const activeMarkup = cacheSwatchMarkup(
        currentHandle,
        container.querySelector('.color-var.active')?.innerHTML ?? ''
      );

      // Renders from Storefront swatch data and the current page's Liquid-rendered markup.
      const renderLinks = () => {
        const fragment = document.createDocumentFragment();
        normalizedHandles.forEach((handle) => {
          const isActive = handle === currentHandle;
          const link = document.createElement('a');
          const markup = isActive ? activeMarkup || buildSwatchHtml(handle) : buildSwatchHtml(handle);

          link.href = '/products/' + handle;
          link.className = `color-var ${isActive ? 'active' : ''}`.trim();
          link.dataset.handle = handle;
          link.innerHTML = markup || '<span></span>';
          fragment.appendChild(link);
        });

        container.replaceChildren(fragment);
      };

      const topInfo = container.closest('.snippet-color-selector');
      if (topInfo) {
        const countEl = topInfo.querySelector('.color-count');
        if (countEl) {
          const n = normalizedHandles.length;
          countEl.textContent = `${n} ${n === 1 ? 'colour' : 'colours'}`;
        }
      }

      renderLinks();
    };

    runPdpInit().catch((err) => {
      console.error('[GroupSwatches] initPdp error:', err);
    });
  };

  /* ── Click handler ───────────────────────────────────────── */

  document.addEventListener('click', async (e) => {
    const swatch = e.target?.closest('.snippet-product-grid .color-var.click[data-handle]');
    if (!swatch) return;

    e.preventDefault();

    const snippetEl = swatch.closest('.snippet-product-grid');
    if (!snippetEl) return;

    const newHandle = swatch.dataset.handle;
    const tag = snippetEl.dataset.groupTag;
    if (!newHandle || !tag) return;

    const cached = groupHandles.get(tag);
    if (cached) {
      swapCard(snippetEl, newHandle, cached);
      return;
    }

    try {
      const handles = await fetchGroup(tag);
      swapCard(snippetEl, newHandle, handles);
    } catch (err) {
      console.error('[GroupSwatches] click handler error:', err);
    }
  });

  // PDP auto-init
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (document.querySelector('.snippet-color-selector')) initPdp();
    });
  } else if (document.querySelector('.snippet-color-selector')) {
    initPdp();
  }

  window.GroupSwatches = { init, initPdp };
  window.dispatchEvent(new CustomEvent('groupswatches:ready', { detail: window.GroupSwatches }));
}
